import { SearchableSelect } from "@/components/molecules/searchable-select";
import type { SearchableSelectItem } from "@/components/molecules/searchable-select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { ExternalAccountsAPI } from "@/features/external-accounts/api/external-accounts";
import { useExternalAccounts } from "@/features/external-accounts/hooks/use-external-accounts";
import { useOAuthPopup } from "@/features/external-accounts/hooks/use-oauth-popup";
import { useOAuthErrorMessage } from "@/features/external-accounts/oauth-errors";
import type { ExternalAccountCapability } from "@/features/external-accounts/types";
import { useTranslation } from "@/features/utils/i18n";
import { GoogleLogoIcon, PlugsConnectedIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import type { CustomFieldProps } from "./dynamic-form";

/**
 * Picker over the client's connected accounts, for settings that name a
 * credential rather than carry one.
 *
 * It also connects one on the spot. A configuration screen that can only say
 * "no account available" sends the user to another page, to come back and start
 * their form again — and the setting they were in the middle of enabling is
 * exactly the moment they learn they need the account. So the empty state is a
 * button, not a warning.
 */
export function ExternalAccountField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  isRequired,
}: CustomFieldProps) {
  const { t } = useTranslation("app");
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const { data: accounts = [], isLoading } = useExternalAccounts();
  const openOAuthPopup = useOAuthPopup();
  const oauthErrorMessage = useOAuthErrorMessage();
  const [connecting, setConnecting] = useState(false);

  const provider = fieldSchema.provider;
  // What the setting needs the account to do; "calendar" is what it always meant.
  const capability = (fieldSchema.capability ??
    "calendar") as ExternalAccountCapability;
  const label = fieldSchema.title || fieldName;

  const usable = accounts.filter(
    (account) =>
      account.status === "active" &&
      (!provider || account.provider === provider) &&
      account.capabilities?.capabilities?.includes(capability),
  );

  const items: SearchableSelectItem[] = usable.map((account) => ({
    id: account.id,
    label: account.provider_identifier || account.name,
    description: account.name,
    icon:
      account.provider === "google" ? (
        <GoogleLogoIcon className="h-4 w-4" />
      ) : undefined,
  }));

  const handleConnect = async () => {
    if (!provider || (provider !== "google" && provider !== "microsoft")) {
      return;
    }

    setConnecting(true);
    try {
      // The URL is handed to the popup unresolved on purpose — see
      // `useOAuthPopup`: awaiting it here is what gets the window blocked.
      // The hook checks the accounts list before calling it a success, so
      // `accountId` is an account that exists, not a window that closed.
      const result = await openOAuthPopup(
        ExternalAccountsAPI.getAuthorizationUrl(clientId, provider, [
          "calendar",
        ]),
      );

      if (!result.success || !result.accountId) {
        toast.error(oauthErrorMessage(result));
        return;
      }

      // Select what the flow brought in, so the user does not have to find
      // their own account in the list.
      onChange(result.accountId);
      toast.success(t("externalAccounts.messages.oauthConnected", ""));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.oauthError", ""),
      );
    } finally {
      setConnecting(false);
    }
  };

  const canConnect = provider === "google" || provider === "microsoft";

  return (
    <div className="space-y-2">
      <Label htmlFor={fieldName}>
        {label}
        {isRequired && <span className="text-destructive ml-1">*</span>}
      </Label>
      {fieldSchema.description && (
        <p className="text-sm text-muted-foreground">
          {fieldSchema.description}
        </p>
      )}

      {usable.length === 0 && !isLoading ? (
        <div className="flex flex-col gap-2 rounded-md border border-dashed p-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "tools.externalAccount.none",
              "No connected account yet for this setting.",
            )}
          </p>
          {canConnect && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={connecting || !clientId}
              onClick={handleConnect}
            >
              <PlugsConnectedIcon className="h-4 w-4 mr-2" />
              {t("tools.externalAccount.connect", "Connect an account")}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <SearchableSelect
              items={items}
              value={typeof value === "string" ? value : ""}
              onChange={(next) => onChange(next as string)}
              placeholder={t(
                "tools.externalAccount.placeholder",
                "Select an account...",
              )}
              isLoading={isLoading}
              allowNone={true}
              noneOption={{
                id: "",
                label: t("tools.externalAccount.noneOption", "None"),
              }}
            />
          </div>
          {canConnect && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={connecting}
              onClick={handleConnect}
            >
              <PlugsConnectedIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
